import React from 'react';
import { wrapLabel, getTextWidth, breakString } from '../../helpers';

const polarToX = (angle, distance) => Math.cos(angle - Math.PI / 2) * distance;

const polarToY = (angle, distance) => Math.sin(angle - Math.PI / 2) * distance;

const points = (points) => {
  return points.map((point) => point[0].toFixed(4) + ',' + point[1].toFixed(4)).join(' ');
};

const axis = (options) => (col, i) => (
  <polyline
    key={`poly-axis-${i}`}
    points={points([
      [0, 0],
      [polarToX(col.angle, options.chartSize / 2), polarToY(col.angle, options.chartSize / 2)],
    ])}
    stroke='#555'
    strokeWidth='.2'
    {...options.axisProps(col)}
  />
);

const dot = (columns, options) => (chartData, i) => {
  const data = chartData.data;
  const meta = chartData.meta || {};
  const extraProps = options.dotProps(meta);
  let mouseEnter = () => {};
  let mouseLeave = () => {};
  if (extraProps.mouseEnter) {
    mouseEnter = extraProps.mouseEnter;
  }
  if (extraProps.mouseLeave) {
    mouseLeave = extraProps.mouseLeave;
  }
  return columns.map((col) => {
    const val = data[col.key];
    // if ('number' !== typeof val) {
    //   throw new Error(`Data set ${i} is invalid.`);
    // }
    // 'undefined' == typeof val
    return !isNaN(val) && (
      <circle
        key={`dot-${col.key}-${val}`}
        cx={polarToX(col.angle, (val * options.chartSize) / 2)}
        cy={polarToY(col.angle, (val * options.chartSize) / 2)}
        fill={meta.color}
        stroke='none'
        r='5'
        className={[extraProps.className, meta.class].join(' ')}
        onMouseEnter={() => mouseEnter({ key: col.key, value: val, idx: i })}
        onMouseLeave={() => mouseLeave({})}
      />
    );
  });
};

const shape = (columns, options) => (chartData, i) => {
  const data = chartData.data;
  const meta = chartData.meta || {};
  const extraProps = options.shapeProps(meta);
  return (
    <path
      key={`shape-${i}`}
      d={options.smoothing(
        columns.map((col) => {
          const val = data[col.key];

          // if ('number' !== typeof val) {
          //   throw new Error(`Data set ${i} is invalid.`);
          // }

          return [polarToX(col.angle, (val * options.chartSize) / 2), polarToY(col.angle, (val * options.chartSize) / 2)];
        })
      )}
      {...extraProps}
      stroke={meta.color}
      fill='none'
      className={[extraProps.className, meta.class].join(' ')}
    />
  );
};

const scale = (options, value) => (
  <circle key={`circle-${value}`} cx={0} cy={0} r={(value * options.chartSize) / 2} fill='none' stroke='#999' strokeWidth='.2' {...options.scaleProps(value)} />
);
const scaleNumberBG = (options, value) => (
  <circle
    key={`circle-text-bg-${value}`}
    cx={polarToX(0.7853981634, (value * options.chartSize) / 2)}
    cy={polarToY(0.7853981634, (value * options.chartSize) / 2)}
    r={8}
    fill='#999'
    stroke='none'
  />
);
const scaleNumber = (options, value, text) => (
  <text
    key={`circle-text-${value}`}
    x={polarToX(0.7853981634, (value * options.chartSize) / 2)}
    y={polarToY(0.7853981634, (value * options.chartSize) / 2)}
    dx={-2}
    dy={2.5}
    fontWeight='bold'
    fontSize='8px'
    fill='#FFF'
  >
    {text}
  </text>
);

const caption = (options) => (col, colIndex, arr) => {
  const {angle, caption} = col;
  let label = wrapLabel(caption, 90)

  if (label.length > 3) {
    // top caption can be wider but if still too many lines then becomes key
    if (angle === 0) {
      label = wrapLabel(caption, 150);
    } 
  }

  const captionLength = col.caption.length;

  const labelLength = label.length;
  const captionKey = captionLength < 4;
  const captionsCount = arr.length;
  const degrees = (360 / captionsCount) * colIndex;


  let yMultiplier = 0.85;
  let xMultiplier = 0.85;
  // Calculates distances for captions in their respective positions, to keep things more consistent
  if (captionKey) {
    yMultiplier = 0.75;
    xMultiplier = 0.75;
  } else {
    if (degrees === 90 || degrees === 270) {
      xMultiplier = 0.86;
    } else if (degrees === 180) {
      yMultiplier = 0.78;
    } else if (degrees === 0) {
      yMultiplier = 0.62 + (0.045 * labelLength)
    } else if (degrees < 90 || degrees > 270) {
      let baseMultiplier = 0.55;
      if (captionsCount > 6) {
        baseMultiplier = 0.62;
      }
      yMultiplier = baseMultiplier + (0.05 * labelLength)
    } else {
      yMultiplier = 0.80;
    }
  }

  let xPos = +polarToX(col.angle, (options.size / 2) * xMultiplier).toFixed(4);
  let yPos = +polarToY(col.angle, (options.size / 2) * yMultiplier).toFixed(4);
  if (captionKey) {
    yPos += 19;
  } else { 
    // adjusts y position for bottom caption, accounting for how many lines it works
    if (degrees === 90 || degrees === 270) {
      yPos += 26 - (labelLength * 7)
    }
  }

  return (
    <text
      key={`caption-of-${col.key}`}
      x={xPos}
      y={yPos}
      dy={(options.captionProps(col).fontSize || 10) / 2}
      fill='#444'
      fontWeight='normal'
      {...options.captionProps(col)}
    >

      {label.map((word, index) => {
        return (
          <tspan key={`colCaption${word}${index}`} x={xPos} dy={index === 0 ? -14 : 14}>
            {word}
          </tspan>
        )
      })}
    </text>
  );
};

// const caption = options => col => (
//   <text
//     key={`caption-of-${col.key}`}
//     x={polarToX(col.angle, (options.size / 2) * 0.95).toFixed(4)}
//     y={polarToY(col.angle, (options.size / 2) * 0.95).toFixed(4)}
//     dy={(options.captionProps(col).fontSize || 10) / 2}
//     fill="#444"
//   	fontWeight="normal"
//     fontSize="4px"
//     {...options.captionProps(col)}
//   >
//     {/*col.caption*/}
//
//   </text>
// );

const render = (captions, chartData, options = {}) => {
  if ('object' !== typeof captions || Array.isArray(captions)) {
    throw new Error('caption must be an object');
  }
  if (!Array.isArray(chartData)) {
    throw new Error('data must be an array');
  }
  options.chartSize = options.size / options.zoomDistance;

  // arrange chart to use established competency sort order where possible
  let captionIDs;
  if (options.captionsOrder) {
    captionIDs = options.captionsOrder;
  } else {
    captionIDs = Object.keys(captions);
  }

  const columns = captionIDs.map((key, i, all) => {
    return {
      key,
      caption: captions[key],
      angle: (Math.PI * 2 * i) / all.length,
    };
  });
  const groups = [<g key={`g-groups}`}>{chartData.map(shape(columns, options))}</g>];
  if (options.captions) {
    groups.push(<g key={`poly-captions`}>{columns.map(caption(options))}</g>);
  }
  if (options.dots) {
    groups.push(<g key={`g-dots`}>{chartData.map(dot(columns, options))}</g>);
  }
  if (options.axes) {
    groups.unshift(<g key={`group-axes`}>{columns.map(axis(options))}</g>);
  }
  if (options.scales > 0) {
    const scaleNumbers = [];
    const scaleNumberBGs = [];
    for (let i = options.scales; i > 0; i--) {
      // console.log(i);
      scaleNumbers.push(scaleNumber(options, i / options.scales, i));
      scaleNumberBGs.push(scaleNumberBG(options, i / options.scales));
      // // console.log(i);
      // // console.log(options, i / options.scales);
    }
    groups.unshift(<g key={`poly-scale-numbers`}>{scaleNumbers}</g>);
    groups.unshift(<g key={`poly-scale-number-bgs`}>{scaleNumberBGs}</g>);

    const scales = [];
    for (let i = options.scales; i > 0; i--) {
      scales.push(scale(options, i / options.scales));
    }
    groups.unshift(<g key={`poly-scales`}>{scales}</g>);
  }
  const delta = (options.size / 2).toFixed(4);
  return <g transform={`translate(${delta},${delta})`}>{groups}</g>;
};

export default render;
